import type { Chord } from '@tunasong/music-lib'
import { ChordLib, getAllChords, GuitarLib, ScaleLib } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { HBox, Tab, Tabs, TextField, Typography } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, SyntheticEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FindGuitarChord } from '../guitar/find-chord/index.js'
import { Note } from '../note/index.js'
import ChordLibrary from './chord-library.js'
import styles from './chord-chooser.module.css'

import type { HTMLAttributes } from 'react'

export interface ChordChooserProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  chord?: Chord
  scale: Scale | null

  onChange(chord: Chord): void
  /** Insert a chord at once: Enter on a name that matches a chord exactly (GH #2488) */
  onInsert?(chord: Chord): void
}

export const ChordChooser: FC<ChordChooserProps> = props => {
  const { chord, scale, onChange, onInsert, ...divProps } = props
  /** "Chord name": on a phone the keyboard is already up, and scrolling hundreds of diagrams is the dead end */
  const [query, setQuery] = useState('')
  const filtered = query.trim() ? ChordLib.filterChordsByName(query, getAllChords()) : undefined

  const [currentNotes, setCurrentNotes] = useState<NoteName[]>(chord ? ChordLib.getNotes(chord) : [])

  const handleGuitarChord = (chord: GuitarLib.GuitarChord) => {
    /** onChange is a partial update, so we need to clear root and variant.   */
    onChange(chord)

    setCurrentNotes(ChordLib.getNotes(chord))
  }

  // const handleChangeRoot = (ev: SelectChangeEvent) => setRoot(ev.target.value)
  type TabValue = 'inscale' | 'all' | 'fretboard' | 'suggestions'
  const initialTab = scale ? 'inscale' : 'all'
  const [tab, setTab] = useState<TabValue>(initialTab)
  const handleTab = useCallback((_ev: SyntheticEvent | null, value: string | number) => {
    setTab(value as TabValue)
    setCurrentNotes([])
  }, [])

  useEffect(() => {
    if (!scale && tab === 'inscale') {
      setTab('all')
    }
  }, [scale, tab])

  const handleQueryKey = (ev: KeyboardEvent) => {
    if (ev.key !== 'Enter') {
      return
    }
    ev.preventDefault()
    const typed = ChordLib.chordForTypedName(query, getAllChords())
    if (!typed) {
      return
    }
    onChange(typed)
    onInsert?.(typed)
  }

  return (
    <div {...divProps}>
      <TextField
        label="Chord name"
        placeholder="e.g. Am7"
        autoFocus
        fullWidth
        value={query}
        onChange={ev => setQuery(ev.target.value)}
        onKeyDown={handleQueryKey}
      />
      <HBox className={styles.headerBox}>
        {currentNotes ? (
          <div>
            {chord ? (
              <Typography variant="body2" component="span" className={styles.chordNameLabel}>
                Notes in {ChordLib.getName(chord)}:
              </Typography>
            ) : null}
            {currentNotes.map((n, idx) => (
              <Note className={styles.noteChip} key={idx} note={n} scale={scale} />
            ))}
          </div>
        ) : (
          <Typography variant="body2">N/A</Typography>
        )}
      </HBox>
      {filtered ? (
        filtered.length > 0 ? (
          <ChordLibrary selected={chord} scale={scale} chords={filtered} onChange={handleGuitarChord} />
        ) : (
          <Typography variant="body2" role="status">
            No chord called &quot;{query.trim()}&quot;. Try a name like Am7 or F#m.
          </Typography>
        )
      ) : null}
      {filtered ? null : (
        <>
          <Tabs className={styles.tabs} value={tab} onChange={handleTab}>
            {/* A song without a key has no in-key chords to offer (GH #2488) */}
            {scale ? <Tab label={ScaleLib.getName(scale)} value={'inscale'} /> : null}
            <Tab label={`All Chords`} value={'all'} />
            <Tab label={`Fretboard`} value={'fretboard'} />
          </Tabs>
          {tab === 'inscale' ? (
            <ChordLibrary
              selected={chord}
              scale={scale}
              chords={ScaleLib.getAllChords(scale)}
              onChange={handleGuitarChord}
            />
          ) : tab === 'all' ? (
            <ChordLibrary selected={chord} scale={scale} onChange={handleGuitarChord} />
          ) : tab === 'fretboard' ? (
            <FindGuitarChord
              className={styles.fretboard}
              defaultChord={GuitarLib.isGuitarChord(chord) ? chord : undefined}
              scale={scale}
              onSelected={handleGuitarChord}
            />
          ) : tab === 'suggestions' ? (
            'Suggestions are coming soon!'
          ) : null}
        </>
      )}
    </div>
  )
}

export default ChordChooser
